// Single source of truth for the Compass Kids Safety Rules copy.
// Rendered on the standalone /safety-rules page and inside the RSVP agreement
// modals on Events and Meet-ups so the two can never drift apart.
export const SAFETY_RULES_VERSION = "2026-02-15";

const RULES = [
  {
    title: "First-time meet-ups & personal safety",
    body: (
      <>
        <p>When meeting other families or attending a meet-up for the first time:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Always meet in a public, open space (a public park, family café or established community venue).</li>
          <li>Never meet for the first time at a personal home or private residence.</li>
          <li>Tell a friend or family member where you are going, who you are meeting and when you expect to return. Share your live location if possible.</li>
          <li>Always trust your instincts — if a location or interaction feels unsafe or uncomfortable, leave immediately.</li>
        </ul>
      </>
    ),
  },
  {
    title: "You stay responsible for your own children",
    body: (
      <p>
        Compass Kids does not provide childcare or supervision. By RSVPing to any event or meet-up,
        you confirm that you (or a nominated adult you trust) will be present and actively supervising
        your children for the full duration.
      </p>
    ),
  },
  {
    title: "Respect every family",
    body: (
      <p>
        Treat every parent, child, host and vendor with kindness. No harassment, discrimination,
        intimidation or inappropriate contact — on the platform, in chat or at the event itself.
        We will immediately remove accounts that break this rule.
      </p>
    ),
  },
  {
    title: "Keep children's details private",
    body: (
      <p>
        Do not share other families' photos, surnames, exact home locations or contact details
        outside the platform without their explicit permission. The attendee list on every meet-up
        intentionally hides personal identifiers for this reason.
      </p>
    ),
  },
  {
    title: "Health, allergies and consent",
    body: (
      <p>
        Flag any serious allergies, medical conditions or access needs to the host via the
        "Note for the host" field before attending. If a child or family member is unwell,
        please stay home — a quick message to the host is appreciated.
      </p>
    ),
  },
  {
    title: "Public spaces and local laws",
    body: (
      <p>
        All community meet-ups must take place in verified public venues, parks or family-friendly
        establishments. Always follow the venue's rules, local municipal laws and any safety
        guidance provided by the host or facility. Hosts and Compass Kids are not liable for
        incidents that occur at third-party venues.
      </p>
    ),
  },
  {
    title: "Report concerns early",
    body: (
      <p>
        If something feels off — a suspicious message, an unverified vendor or inappropriate
        behaviour from another member — email{" "}
        <a href="mailto:info@compasskids.co.za" className="font-bold text-terracotta-dark hover:underline">info@compasskids.co.za</a>{" "}
        immediately so we can investigate. For any active emergency or threat to personal safety,
        always contact local emergency services (10111 / 112) first.
      </p>
    ),
  },
  {
    title: "Photos and recording",
    body: (
      <p>
        Always ask every parent in the frame before posting group photos or videos online or on
        social media. Respect a parent's wish to keep their child out of photos. Hosts will usually
        clarify upfront whether photos are welcomed during the activity.
      </p>
    ),
  },
  {
    title: "Agreement to these rules",
    body: (
      <p>
        You confirm "I agree to the Compass Kids safety rules" each time you create or RSVP to a
        meet-up or event. By continuing to use Compass Kids, you confirm that you have read,
        understood and will strictly adhere to these rules.
      </p>
    ),
  },
];

/**
 * Renders the Compass Kids Safety Rules body. Caller controls the wrapping
 * container (page, dialog, popover) so this component stays presentational.
 *
 * @param {object} props
 * @param {("page"|"compact")} [props.variant="page"] — "compact" tightens the
 *   headings/spacing so the content fits inside dialogs.
 * @param {string} [props.testIdPrefix="safety-rules"] — scoped test-id prefix.
 */
export default function SafetyRulesContent({ variant = "page", testIdPrefix = "safety-rules" }) {
  const compact = variant === "compact";
  return (
    <div data-testid={`${testIdPrefix}-content`}>
      <p className={`${compact ? "text-xs" : ""} text-forest/70 leading-relaxed ${compact ? "mb-3" : "mb-6"}`}>
        Compass Kids is a community platform that helps families find and attend meet-ups,
        events and vendor activities. Everyone who joins a Compass Kids gathering agrees to
        the simple rules below so the whole community stays safe and welcoming.
      </p>
      <ol className={`${compact ? "space-y-3" : "space-y-5"} text-forest/80 leading-relaxed`}>
        {RULES.map((rule, i) => (
          <li key={rule.title} data-testid={`${testIdPrefix}-rule-${i + 1}`}>
            <h3 className={`${compact ? "text-sm" : "text-xl"} font-black text-forest ${compact ? "mb-1" : "mb-2"}`}>
              {i + 1}. {rule.title}
            </h3>
            <div className={compact ? "text-xs" : ""}>{rule.body}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}
